import { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Box, Typography } from "@mui/material";
import { CloudUploadOutlined } from "@mui/icons-material";

import { Loader } from "components";

import { useToaster } from "hooks";

import PropTypes from "prop-types";
import theme from "themeProvider";
import { error } from "constants";

/**
 *
 * @description PDF/Image Upload component with max file size check & callback function prop
 */
const UploadPdf = forwardRef(
  (
    {
      headerText,
      fileCallBackFn,
      allowedMaxSize = 2,
      isLoading = false,
      customStyle,
    },
    ref,
  ) => {
    const fileUpload = useRef(null);
    const [file, setFile] = useState();
    const triggerToaster = useToaster();

    const handleClick = () => {
      if (!isLoading) {
        fileUpload.current?.click();
      } else {
        triggerToaster(
          `The upload for the file ${file?.name} is in progress. Two simultaneous file uploads are not allowed.`,
          error,
        );
      }
    };

    const handleDragOver = (e) => e.preventDefault();

    /**
     * @param {*} event
     * @description handle all file validations & invoke file callback
     */
    const handleFileUpload = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const allowedFileTypes = ["application/pdf", "image/png", "image/jpeg"];

      const file = [
        ...(e.type === "drop" ? e.dataTransfer.files : e.target.files),
      ];

      if (file.length > 1) {
        triggerToaster(`Only 1 file can be uploaded at a time`, error);
        return;
      }
      const fileSize = file?.[0]?.size / 1024 / 1024; //convert to MB

      if (fileSize.toFixed(2) > allowedMaxSize) {
        triggerToaster(`Allowed max file size is ${allowedMaxSize}MB`, error);
        return;
      }

      if (!allowedFileTypes.includes(file?.[0]?.type)) {
        triggerToaster(`Please upload a PDF, PNG, or JPEG file`, error);
        return;
      }
      if (file && file.length) {
        setFile(file?.[0]);
        fileCallBackFn?.(file?.[0]);
      }
    };

    // hook to expose function - which will return file and reset file
    useImperativeHandle(ref, () => ({
      getFile: () => file,
      resetFile: () => setFile(null),
    }));

    return (
      <>
        {!!headerText && (
          <Typography fontSize={18} fontWeight={"bold"} mb={4}>
            {headerText}
          </Typography>
        )}
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          style={{
            width: "100%",
            border: 1,
            borderStyle: "dashed",
            borderColor: theme.palette.grey[900],
            borderRadius: 12,
            backgroundColor: theme.palette.info.lightBlueBackround,
            ...customStyle,
          }}
          mb={2}
          p={12}
          pt={isLoading ? 0 : 4}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDrop={handleFileUpload}
        >
          {isLoading ? (
            <Box>
              <Loader sx={{ marginBottom: 20 }} />
              <Typography>
                Uploading <b> {file?.name || ""}</b>
              </Typography>
            </Box>
          ) : (
            <>
              {/* OnClick file upload */}
              <input
                style={{ display: "none" }}
                type="file"
                data-testid="upload-input"
                ref={fileUpload}
                accept="application/pdf,image/png,image/jpeg"
                onClick={(event) => {
                  event.target.value = null;
                }}
                onChange={handleFileUpload}
              />
              <Box
                sx={{
                  backgroundColor: theme.palette.common?.white || "#fff",
                  borderRadius: "50%",
                  width: 64,
                  height: 64,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  mb: 2,
                }}
              >
                <CloudUploadOutlined fontSize="large" />
              </Box>
              {file && <Typography marginBottom={2}>{file?.name}</Typography>}
              {!file && (
                <>
                  <Typography style={{ fontSize: 16, lineHeight: 1.25 }}>
                    Click or drag file to this area to upload
                  </Typography>
                  <Typography
                    style={{
                      fontSize: 14,
                      color: theme.palette.grey[1000],
                      lineHeight: 1.2,
                    }}
                  >
                    Accepted file formats are PDF/PNG/JPEG. Maximum file size{" "}
                    {allowedMaxSize} MB.
                  </Typography>
                </>
              )}
            </>
          )}
        </Box>
      </>
    );
  },
);

UploadPdf.propTypes = {
  headerText: PropTypes.string,
  fileCallBackFn: PropTypes.func,
  allowedMaxSize: PropTypes.number,
  isLoading: PropTypes.bool,
};

export default UploadPdf;
